/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Factory, Search, Globe, Bot, User, Sparkles, ChevronRight,
  Edit2, FileText, Plus,
} from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";

const tenantDb = supabase as any;

// Status taxonomy. Labels are now resolved from i18n at render time; the
// colour classes stay co-located here since they're styling, not copy.
const FACTORY_STAGE_KEYS = ["draft", "review", "revision", "approved", "scheduled"] as const;
const ALL_STATUS_KEYS = [...FACTORY_STAGE_KEYS, "published", "archived"] as const;

const STAGE_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  review: "bg-warning/20 text-warning",
  revision: "bg-accent text-accent-foreground",
  approved: "bg-primary/20 text-primary",
  scheduled: "bg-primary/20 text-primary",
  published: "bg-emerald-500/20 text-emerald-600",
  archived: "bg-destructive/20 text-destructive",
};

export default function ContentFactoryPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentCompanyId } = useCompany();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [filterWebsite, setFilterWebsite] = useState<string>("all");

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["content-factory-articles", currentCompanyId],
    queryFn: async () => {
      const { data, error } = await tenantDb
        .from("articles")
        .select("*, profiles(full_name), categories(name), article_publications(website_id, status, websites(name, domain))")
        .eq("company_id", currentCompanyId)
        .in("status", [...FACTORY_STAGE_KEYS])
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user && !!currentCompanyId,
  });

  const { data: websites = [] } = useQuery({
    queryKey: ["websites-list", currentCompanyId],
    queryFn: async () => {
      const { data } = await tenantDb.from("websites").select("id, name, domain").eq("company_id", currentCompanyId).order("name");
      return data || [];
    },
    enabled: !!currentCompanyId,
  });

  const { data: importLog = [] } = useQuery({
    queryKey: ["news-import-log", currentCompanyId],
    queryFn: async () => {
      const { data } = await tenantDb.from("news_import_log").select("article_id").eq("company_id", currentCompanyId);
      return data || [];
    },
    enabled: !!user && !!currentCompanyId,
  });
  const importedIds = new Set(importLog.map((l: any) => l.article_id));

  const { data: autoGenLogs = [] } = useQuery({
    queryKey: ["automation-logs-complete", currentCompanyId],
    queryFn: async () => {
      const { data } = await tenantDb
        .from("automation_logs")
        .select("article_id")
        .eq("company_id", currentCompanyId)
        .eq("step", "complete")
        .eq("status", "success")
        .not("article_id", "is", null);
      return data || [];
    },
    enabled: !!user && !!currentCompanyId,
  });
  const autoGeneratedIds = new Set(autoGenLogs.map((l: any) => l.article_id));

  const statusMutation = useMutation({
    mutationFn: async ({ articleId, newStatus }: { articleId: string; newStatus: string }) => {
      const { error } = await tenantDb.from("articles").update({ status: newStatus as any }).eq("id", articleId).eq("company_id", currentCompanyId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["content-factory-articles"] });
      queryClient.invalidateQueries({ queryKey: ["articles-library"] });
      toast({ title: t("contentFactory.toasts.statusUpdated") });
    },
    onError: (err: any) => {
      toast({ title: t("contentFactory.toasts.error"), description: err.message, variant: "destructive" });
    },
  });

  const filtered = articles.filter((a: any) => {
    const matchSearch = a.title.toLowerCase().includes(search.toLowerCase());
    const matchWebsite =
      filterWebsite === "all" ||
      a.article_publications?.some((p: any) => p.website_id === filterWebsite) ||
      a.website_id === filterWebsite;
    return matchSearch && matchWebsite;
  });

  const grouped = FACTORY_STAGE_KEYS.map((key) => ({
    key,
    label: t(`contentFactory.stages.${key}`),
    color: STAGE_COLORS[key],
    articles: filtered.filter((a: any) => a.status === key),
  }));

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 backdrop-blur-xl px-6">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{t("contentFactory.title")}</h1>
          <p className="text-xs text-muted-foreground">
            {t("contentFactory.subtitle", { count: articles.length })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/articles">
            <Button variant="outline" size="sm"><FileText className="h-4 w-4 mr-1" /> {t("contentFactory.library")}</Button>
          </Link>
          <Link to="/articles/new">
            <Button size="sm"><Plus className="h-4 w-4 mr-1" /> {t("contentFactory.newArticle")}</Button>
          </Link>
        </div>
      </header>

      <div className="p-6 space-y-4 animate-slide-in">
        <div className="grid grid-cols-5 gap-3">
          {grouped.map((stage) => (
            <div key={stage.key} className="rounded-lg border border-border bg-card p-3 text-center">
              <p className="text-xl font-bold text-foreground">{stage.articles.length}</p>
              <p className={`text-[10px] font-medium uppercase tracking-wide ${stage.color.split(" ")[1]}`}>{stage.label}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder={t("contentFactory.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
          </div>
          <Select value={filterWebsite} onValueChange={setFilterWebsite}>
            <SelectTrigger className="w-52">
              <Globe className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
              <SelectValue placeholder={t("contentFactory.allWebsites")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("contentFactory.allWebsites")}</SelectItem>
              {websites.map((w: any) => (
                <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="flex gap-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex-shrink-0 w-60 space-y-2">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-28 w-full" />
                <Skeleton className="h-28 w-full" />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-4">
            {grouped.map((stage) => (
              <div key={stage.key} className="flex-shrink-0 w-64">
                <div className="flex items-center gap-2 mb-2 px-1">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${stage.color}`}>{stage.label}</span>
                  <span className="text-xs text-muted-foreground font-medium">{stage.articles.length}</span>
                </div>
                <div className="space-y-2 min-h-[200px] rounded-lg border border-dashed border-border/50 bg-muted/20 p-2">
                  {stage.articles.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-8">{t("contentFactory.noArticles")}</p>
                  ) : (
                    stage.articles.map((article: any) => (
                      <PipelineCard
                        key={article.id}
                        article={article}
                        isImported={importedIds.has(article.id)}
                        isAutoGenerated={autoGeneratedIds.has(article.id)}
                        onStatusChange={(newStatus) => statusMutation.mutate({ articleId: article.id, newStatus })}
                      />
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PipelineCard({ article, isImported, isAutoGenerated, onStatusChange }: {
  article: any; isImported: boolean; isAutoGenerated: boolean; onStatusChange: (status: string) => void;
}) {
  const { t } = useTranslation();
  const pubs = article.article_publications || [];

  return (
    <div className="rounded-lg border border-border bg-card p-3 space-y-2 hover:border-primary/30 transition-colors group">
      <Link to={`/articles/${article.id}`} className="block">
        <p className="text-sm font-medium text-foreground line-clamp-2 group-hover:text-primary transition-colors">{article.title}</p>
      </Link>
      <div className="flex items-center gap-1.5 flex-wrap">
        {isAutoGenerated ? (
          <Badge variant="outline" className="text-[9px] gap-1 px-1.5 py-0 border-emerald-500/30 text-emerald-600"><Sparkles className="h-2.5 w-2.5" /> {t("contentFactory.badges.autoGenerated")}</Badge>
        ) : isImported ? (
          <Badge variant="outline" className="text-[9px] gap-1 px-1.5 py-0 border-primary/30 text-primary"><Bot className="h-2.5 w-2.5" /> {t("contentFactory.badges.imported")}</Badge>
        ) : (
          <Badge variant="outline" className="text-[9px] gap-1 px-1.5 py-0"><User className="h-2.5 w-2.5" /> {t("contentFactory.badges.manual")}</Badge>
        )}
        {(article.status === "review" || article.status === "revision") && (
          <Badge variant="outline" className="text-[9px] gap-1 px-1.5 py-0 border-purple-500/30 text-purple-500 animate-pulse"><Sparkles className="h-2.5 w-2.5" /> {t("contentFactory.badges.ai")}</Badge>
        )}
        {pubs.length > 0 && (
          <Badge variant="secondary" className="text-[9px] gap-1 px-1.5 py-0"><Globe className="h-2.5 w-2.5" /> {pubs.length}</Badge>
        )}
      </div>
      {pubs.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {pubs.slice(0, 3).map((p: any) => (
            <span key={p.website_id} className="text-[9px] text-muted-foreground bg-muted rounded px-1.5 py-0.5 truncate max-w-[100px]">{p.websites?.name || "—"}</span>
          ))}
          {pubs.length > 3 && <span className="text-[9px] text-muted-foreground">+{pubs.length - 3}</span>}
        </div>
      )}
      <div className="flex items-center justify-between pt-1 border-t border-border/50">
        <span className="text-[10px] text-muted-foreground truncate max-w-[100px]">{article.profiles?.full_name || t("contentFactory.author")}</span>
        <div className="flex items-center gap-1">
          <Link to={`/articles/${article.id}`}>
            <IconButton variant="ghost" className="h-5 w-5" label={t("actions.edit")}><Edit2 className="h-3 w-3" /></IconButton>
          </Link>
          <Select value={article.status} onValueChange={onStatusChange}>
            <SelectTrigger className="h-5 w-auto border-none bg-transparent text-[10px] p-0 pr-4 focus:ring-0"><ChevronRight className="h-3 w-3" /></SelectTrigger>
            <SelectContent>
              {ALL_STATUS_KEYS.map((key) => (
                <SelectItem key={key} value={key} className="text-xs">{t(`contentFactory.stages.${key}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
